import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import {
  HelpdeskTicketInterface,
  TicketInternalStatus,
} from "shared/types/helpdesk";
import { useUsers } from "shared/contexts/users.context";
import {
  useHelpdeskDataLazy,
  useHelpdeskListPagination,
  useHelpdeskFilters,
  useHelpdeskBulkActions,
} from "../index";
import type { HelpdeskFilters } from "./useHelpdeskDataLazy";
import { DeleteModalState } from "../types";
import type { FilterSection } from "shared/components/filterBar/types";
import {
  ColumnActionConfig,
  ModalConfig,
} from "shared/components/UniversalTable/types/columnActions";
import { getStatusLabel, helpdeskPhaseOrder } from "../../helpdeskPhaseConfig";
import { Avatar } from "@heroui/react";
import { getUserImageUrlById, getUserName } from "shared/utils/users";
import { Button } from "components/ui/button";

/**
 * Helper functions to read initial filter values from URL parameters
 * This ensures filters are preserved when navigating back from ticket detail
 */
const getFilterFromURL = (key: string): string[] => {
  const params = new URLSearchParams(window.location.search);
  const value = params.get(key);
  return value ? value.split(",").filter(Boolean) : [];
};

const getValueFromURL = (key: string, defaultValue: string = ""): string => {
  const params = new URLSearchParams(window.location.search);
  return params.get(key) || defaultValue;
};

/**
 * Interface for the shared Helpdesk state and actions
 * Used by both HelpdeskListPage and HelpdeskKanbanPage
 */
export interface HelpdeskSharedState {
  /**
   * De filters zoals ze naar de API gaan. De weergave per gebouw haalt haar eigen
   * (gegroepeerde) lijst op en heeft daar exact dezelfde filters voor nodig.
   */
  filters: HelpdeskFilters;

  // Data from API
  nonClosedTickets: HelpdeskTicketInterface[];
  closedTickets: HelpdeskTicketInterface[];
  isLoadingTickets: boolean;
  totalCount: number | null;
  openCount: number | null;
  completionCount: number | null;
  isLoadingTotalCount: boolean;

  // List pagination
  listPage: number;
  setListPage: (page: number) => void;
  listPageSize: number;
  isListLoading: boolean;
  listTotal: number;

  // Computed data
  filteredTickets: HelpdeskTicketInterface[]; // For list view (excludes closed)
  kanbanTickets: HelpdeskTicketInterface[]; // For kanban view (includes closed)
  slicedKanbanTickets: HelpdeskTicketInterface[]; // Sliced by column pagination
  displayedNonClosedTickets: number;
  hasFilters: boolean;

  // Filter states
  searchText: string;
  setSearchText: (value: string) => void;
  sourceFilters: string[];
  setSourceFilters: (value: string[]) => void;
  statusFilters: string[];
  setStatusFilters: (value: string[]) => void;
  priorityFilters: string[];
  setPriorityFilters: (value: string[]) => void;
  buildingFilters: string[];
  setBuildingFilters: (value: string[]) => void;
  unitFilters: string[];
  setUnitFilters: (value: string[]) => void;
  labelFilters: string[];
  setLabelFilters: (value: string[]) => void;
  assigneeFilters: string[];
  setAssigneeFilters: (value: string[]) => void;
  supplierFilters: string[];
  setSupplierFilters: (value: string[]) => void;
  dateFilter: "all" | "today" | "this-week" | "this-month" | "older-than-week";
  setDateFilter: (
    value: "all" | "today" | "this-week" | "this-month" | "older-than-week",
  ) => void;
  emailStatusFilter: string[];
  setEmailStatusFilter: (value: string[]) => void;

  // Filter options from backend
  allBuildings: string[];
  allUnits: string[];
  allLabels: string[];
  allAssignees: string[];
  allSuppliers: string[];

  // Filter configuration
  filterBarSections: FilterSection[];
  filterBarChips: any[];
  updateFilters: (key: string, values: string[]) => void;
  handleFiltersFromURL: (urlFilters: any) => void;
  isRestoringFromURL: boolean;

  // Smart insights
  smartInsightsFilter: any;
  setSmartInsightsFilter: (filter: any) => void;
  clearSmartInsightsFilter: () => void;
  savedSearches: any[];
  saveCurrentSearch: () => void;
  applySavedSearch: (search: any) => void;

  // Actions
  refreshTickets: () => Promise<void>;
  handleCompleteTicket: (ticketId: number) => Promise<void>;
  handleMoveToCompletion: (ticketId: number) => Promise<void>;
  handleRevertToOpen: (ticketId: number) => Promise<void>;
  handleUpdateTicket: (ticketId: number, updates: any) => void;
  handleSmartInsights: (ticket: HelpdeskTicketInterface) => Promise<void>;
  handleMerge: (ticket: HelpdeskTicketInterface) => void;
  handleDelete: (ticket: HelpdeskTicketInterface) => Promise<void>;
  handleCreateInsurance: (ticket: HelpdeskTicketInterface) => void;
  handleQuickAdd: () => void;
  handleTicketClick: (ticket: HelpdeskTicketInterface) => void;

  // Bulk actions
  selectedOpenTickets: Set<number>;
  selectedCompletionTickets: Set<number>;
  setSelectedOpenTickets: (value: Set<number>) => void;
  setSelectedCompletionTickets: (value: Set<number>) => void;
  bulkActions: any[];
  handleBulkPhaseChange: () => void;
  handleBulkPhaseConfirm: (newPhase: TicketInternalStatus) => Promise<void>;
  handleExcelExportWithColumns: (selectedColumnIds: string[]) => void;

  // Kanban-specific states
  visibleCountPerColumn: Record<string, number>;
  setVisibleCountPerColumn: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  hiddenKanbanColumns: string[];
  setHiddenKanbanColumns: React.Dispatch<React.SetStateAction<string[]>>;
  kanbanFontSize: number;
  setKanbanFontSize: React.Dispatch<React.SetStateAction<number>>;
  handleIncreaseFontSize: () => void;
  handleDecreaseFontSize: () => void;
  handleToggleColumnVisibility: (columnId: string) => void;
  allStatusColumns: TicketInternalStatus[];
  columnCountsMap: Record<string, number>;
  getColumnCounts: (status: TicketInternalStatus) => {
    total: number;
    visible: number;
    hasMore: boolean;
  };
  handleLoadMoreForColumn: (status: TicketInternalStatus) => void;
  columnPagination: any[];
  handleColumnHeaderClick: (status: TicketInternalStatus) => void;
  handleColumnPhaseConfirm: (newPhase: TicketInternalStatus) => Promise<void>;

  // Modal states
  quickAddModalOpen: boolean;
  setQuickAddModalOpen: (value: boolean) => void;
  mergeModalOpen: boolean;
  setMergeModalOpen: (value: boolean) => void;
  mergeSource: HelpdeskTicketInterface | null;
  setMergeSource: (value: HelpdeskTicketInterface | null) => void;
  mergeTarget: HelpdeskTicketInterface | null;
  setMergeTarget: (value: HelpdeskTicketInterface | null) => void;
  mergeSearchSource: string;
  setMergeSearchSource: (value: string) => void;
  mergeSearchTarget: string;
  setMergeSearchTarget: (value: string) => void;
  deleteModal: DeleteModalState;
  setDeleteModal: (value: DeleteModalState) => void;
  insuranceModalOpen: boolean;
  setInsuranceModalOpen: (value: boolean) => void;
  selectedTicketForInsurance: HelpdeskTicketInterface | null;
  setSelectedTicketForInsurance: (
    value: HelpdeskTicketInterface | null,
  ) => void;
  supplierSuggestionOpen: boolean;
  setSupplierSuggestionOpen: (value: boolean) => void;
  suggestedSuppliers: any[];
  setSuggestedSuppliers: (value: any[]) => void;
  suggestionForLabel: string | null;
  setSuggestionForLabel: (value: string | null) => void;
  suggestionForTicketIds: number[];
  setSuggestionForTicketIds: (value: number[]) => void;
  isUpdatingSupplier: boolean;
  setIsUpdatingSupplier: (value: boolean) => void;
  excelExportModalOpen: boolean;
  setExcelExportModalOpen: (value: boolean) => void;
  bulkPhasePickerOpen: boolean;
  setBulkPhasePickerOpen: (value: boolean) => void;
  columnPhasePickerOpen: boolean;
  setColumnPhasePickerOpen: (value: boolean) => void;
  selectedColumnPhase: TicketInternalStatus | null;
  setSelectedColumnPhase: (value: TicketInternalStatus | null) => void;

  // Column actions
  columnActions: ColumnActionConfig[];
  handleModalRequest: (event: { config: ModalConfig }) => void;
  applySuggestedSupplier: (supplier: any) => Promise<void>;
  renderBulkActionIcon: (iconType: string) => JSX.Element | null;

  // Users context
  users: any[];
}

/**
 * Shared hook for Helpdesk pages
 *
 * This hook extracts ALL shared logic from HelpdeskTicketsPage.tsx
 * to be reused by both HelpdeskListPage and HelpdeskKanbanPage.
 *
 * It manages:
 * - Filter states and URL synchronization
 * - Data fetching via useHelpdeskDataLazy
 * - Bulk actions and selections
 * - Modal states (merge, delete, insurance, etc.)
 * - Event handlers (delete, merge, smart insights, etc.)
 * - Column actions configuration
 * - Kanban-specific pagination and visibility
 *
 * @param viewMode - 'list' or 'kanban' to determine data fetching strategy
 */
export const useHelpdeskShared = (
  viewMode: "list" | "kanban",
): HelpdeskSharedState => {
  const { session } = useSession();
  const { users } = useUsers();

  // ============ FILTER STATES ============
  // Initialize filter states from URL to prevent race condition on back navigation
  const [searchText, setSearchText] = useState(getValueFromURL("search"));
  const [sourceFilters, setSourceFilters] = useState<string[]>(
    getFilterFromURL("source"),
  );
  const [statusFilters, setStatusFilters] = useState<string[]>(
    getFilterFromURL("status"),
  );
  const [priorityFilters, setPriorityFilters] = useState<string[]>(
    getFilterFromURL("priority"),
  );
  const [buildingFilters, setBuildingFilters] = useState<string[]>(
    getFilterFromURL("building"),
  );
  const [unitFilters, setUnitFilters] = useState<string[]>(
    getFilterFromURL("unit"),
  );
  const [labelFilters, setLabelFilters] = useState<string[]>(
    getFilterFromURL("label"),
  );
  const [assigneeFilters, setAssigneeFilters] = useState<string[]>(
    getFilterFromURL("assignee"),
  );
  const [supplierFilters, setSupplierFilters] = useState<string[]>(
    getFilterFromURL("supplier"),
  );
  const [dateFilter, setDateFilter] = useState<
    "all" | "today" | "this-week" | "this-month" | "older-than-week"
  >(getValueFromURL("dateFilter", "all") as any);
  const [emailStatusFilter, setEmailStatusFilter] = useState<string[]>(
    getFilterFromURL("emailStatus"),
  );

  // Pre-fetched filter options from backend endpoints
  const [allBuildings, setAllBuildings] = useState<string[]>([]);
  const [allUnits, setAllUnits] = useState<string[]>([]);
  const [allLabels, setAllLabels] = useState<string[]>([]);
  const [allAssignees, setAllAssignees] = useState<string[]>([]);
  const [allSuppliers, setAllSuppliers] = useState<string[]>([]);

  // ============ MODAL STATES ============
  const [quickAddModalOpen, setQuickAddModalOpen] = useState(false);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeSource, setMergeSource] =
    useState<HelpdeskTicketInterface | null>(null);
  const [mergeTarget, setMergeTarget] =
    useState<HelpdeskTicketInterface | null>(null);
  const [mergeSearchSource, setMergeSearchSource] = useState("");
  const [mergeSearchTarget, setMergeSearchTarget] = useState("");
  const [deleteModal, setDeleteModal] = useState<DeleteModalState>({
    open: false,
    ticket: null,
  });
  const [insuranceModalOpen, setInsuranceModalOpen] = useState(false);
  const [selectedTicketForInsurance, setSelectedTicketForInsurance] =
    useState<HelpdeskTicketInterface | null>(null);

  // Supplier suggestion modal states
  const [supplierSuggestionOpen, setSupplierSuggestionOpen] = useState(false);
  const [suggestedSuppliers, setSuggestedSuppliers] = useState<any[]>([]);
  const [suggestionForLabel, setSuggestionForLabel] = useState<string | null>(
    null,
  );
  const [suggestionForTicketIds, setSuggestionForTicketIds] = useState<
    number[]
  >([]);
  const [isUpdatingSupplier, setIsUpdatingSupplier] = useState(false);

  // Excel export modal state
  const [excelExportModalOpen, setExcelExportModalOpen] = useState(false);

  // Bulk phase editing modal state
  const [bulkPhasePickerOpen, setBulkPhasePickerOpen] = useState(false);

  // Kanban column phase editing modal state
  const [columnPhasePickerOpen, setColumnPhasePickerOpen] = useState(false);
  const [selectedColumnPhase, setSelectedColumnPhase] =
    useState<TicketInternalStatus | null>(null);

  // ============ KANBAN-SPECIFIC STATES ============
  // Kanban per-column pagination state
  const [visibleCountPerColumn, setVisibleCountPerColumn] = useState<
    Record<string, number>
  >(() => {
    const initialCounts: Record<string, number> = {};
    helpdeskPhaseOrder.forEach((status) => {
      initialCounts[status] = 30;
    });
    return initialCounts;
  });

  // Kanban column visibility state - persisted to localStorage
  const [hiddenKanbanColumns, setHiddenKanbanColumns] = useState<string[]>(
    () => {
      try {
        const saved = localStorage.getItem("helpdesk-kanban-hidden-columns");
        return saved ? JSON.parse(saved) : [];
      } catch {
        return [];
      }
    },
  );

  // Persist hidden columns to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(
        "helpdesk-kanban-hidden-columns",
        JSON.stringify(hiddenKanbanColumns),
      );
    } catch (error) {
      console.error("Failed to save hidden columns to localStorage:", error);
    }
  }, [hiddenKanbanColumns]);

  // Kanban card font size state - persisted to localStorage
  const [kanbanFontSize, setKanbanFontSize] = useState<number>(() => {
    try {
      const saved = localStorage.getItem("helpdesk-kanban-font-size");
      return saved ? parseInt(saved, 10) : 12;
    } catch {
      return 12;
    }
  });

  // Persist font size to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(
        "helpdesk-kanban-font-size",
        kanbanFontSize.toString(),
      );
    } catch (error) {
      console.error("Failed to save font size to localStorage:", error);
    }
  }, [kanbanFontSize]);

  // Initialize sticky top
  useEffect(() => {
    document.documentElement.style.setProperty("--sticky-top", "0px");
  }, []);

  // ============ FETCH FILTER OPTIONS ============
  useEffect(() => {
    const fetchFilterOptions = async () => {
      if (!session) return;

      try {
        const [buildingsRes, unitsRes, labelsRes, assigneesRes, suppliersRes] =
          await Promise.all([
            fetchWithAuth(
              `${API_URL}/helpdesk/filter-options/buildings`,
              { method: "GET" },
              session,
            ),
            fetchWithAuth(
              `${API_URL}/helpdesk/filter-options/units`,
              { method: "GET" },
              session,
            ),
            fetchWithAuth(
              `${API_URL}/helpdesk/filter-options/labels`,
              { method: "GET" },
              session,
            ),
            fetchWithAuth(
              `${API_URL}/helpdesk/filter-options/assignees`,
              { method: "GET" },
              session,
            ),
            fetchWithAuth(
              `${API_URL}/helpdesk/filter-options/suppliers`,
              { method: "GET" },
              session,
            ),
          ]);

        if (buildingsRes.ok) {
          const buildings = await buildingsRes.json();
          setAllBuildings(buildings);
        }
        if (unitsRes.ok) {
          const units = await unitsRes.json();
          setAllUnits(units);
        }
        if (labelsRes.ok) {
          const labels = await labelsRes.json();
          setAllLabels(labels);
        }
        if (assigneesRes.ok) {
          const assignees = await assigneesRes.json();
          setAllAssignees(assignees);
        }
        if (suppliersRes.ok) {
          const suppliers = await suppliersRes.json();
          setAllSuppliers(suppliers);
        }
      } catch (error) {
        console.error("Failed to fetch filter options:", error);
      }
    };

    fetchFilterOptions();
  }, [session]);

  // ============ PREPARE FILTERS FOR API ============
  const filters = useMemo(
    () => ({
      search: searchText,
      source: sourceFilters,
      status: statusFilters,
      priority: priorityFilters,
      building: buildingFilters,
      unit: unitFilters,
      label: labelFilters,
      assignee: assigneeFilters,
      supplier: supplierFilters,
      dateFilter: dateFilter,
      hasUnansweredIncomingEmail: emailStatusFilter.includes("unanswered")
        ? true
        : undefined,
      hasUnreadEmails: emailStatusFilter.includes("unread")
        ? true
        : undefined,
    }),
    [
      searchText,
      sourceFilters,
      statusFilters,
      priorityFilters,
      buildingFilters,
      unitFilters,
      labelFilters,
      assigneeFilters,
      supplierFilters,
      dateFilter,
      emailStatusFilter,
    ],
  );

  // ============ DATA FETCHING ============
  const {
    nonClosedTickets,
    closedTickets,
    isLoadingTickets,
    refreshTickets: refreshTicketsLazy,
    handleCompleteTicket: handleCompleteTicketLazy,
    handleMoveToCompletion: handleMoveToCompletionLazy,
    handleRevertToOpen: handleRevertToOpenLazy,
    handleUpdateTicket: handleUpdateTicketLazy,
    totalCount,
    openCount,
    completionCount,
    isLoadingTotalCount,
  } = useHelpdeskDataLazy(filters, viewMode);

  // ============ LIST VIEW PAGINATION ============
  const {
    listTickets,
    setListTickets,
    listTotal,
    listPage,
    setListPage,
    listPageSize,
    isListLoading,
    fetchListTickets,
  } = useHelpdeskListPagination(filters, viewMode);

  // ============ COMBINED ACTION WRAPPERS ============
  // Wrap lazy-loading actions to also update paginated list data

  const refreshTickets = useCallback(async () => {
    await refreshTicketsLazy();
    if (viewMode === "list") await fetchListTickets();
  }, [refreshTicketsLazy, viewMode, fetchListTickets]);

  const handleUpdateTicket = useCallback(
    (ticketId: number, updates: any) => {
      handleUpdateTicketLazy(ticketId, updates);
      // Also update the paginated list data
      setListTickets((prev: any[]) =>
        prev.map((t: any) => (t.id === ticketId ? { ...t, ...updates } : t)),
      );
    },
    [handleUpdateTicketLazy, setListTickets],
  );

  const handleCompleteTicket = useCallback(
    async (ticketId: number) => {
      await handleCompleteTicketLazy(ticketId);
      if (viewMode === "list") await fetchListTickets();
    },
    [handleCompleteTicketLazy, viewMode, fetchListTickets],
  );

  const handleMoveToCompletion = useCallback(
    async (ticketId: number) => {
      await handleMoveToCompletionLazy(ticketId);
      if (viewMode === "list") await fetchListTickets();
    },
    [handleMoveToCompletionLazy, viewMode, fetchListTickets],
  );

  const handleRevertToOpen = useCallback(
    async (ticketId: number) => {
      await handleRevertToOpenLazy(ticketId);
      if (viewMode === "list") await fetchListTickets();
    },
    [handleRevertToOpenLazy, viewMode, fetchListTickets],
  );

  // ============ FILTER UPDATE HANDLER ============
  const updateFilters = useCallback((key: string, values: string[]) => {
    if (key === "search") {
      return; // Let component-level restoration handle search
    }

    switch (key) {
      case "source":
        setSourceFilters(values);
        break;
      case "status":
        setStatusFilters(values);
        break;
      case "priority":
        setPriorityFilters(values);
        break;
      case "building":
        setBuildingFilters(values);
        break;
      case "unit":
        setUnitFilters(values);
        break;
      case "label":
        setLabelFilters(values);
        break;
      case "assignee":
        setAssigneeFilters(values);
        break;
      case "supplier":
        setSupplierFilters(values);
        break;
      case "date":
        setDateFilter(values.length > 0 ? (values[0] as any) : "all");
        break;
      case "emailStatus":
        setEmailStatusFilter(values);
        break;
    }
  }, []);

  // ============ COMPUTED VALUES ============
  const hasFilters = useMemo(() => {
    return !!(
      searchText ||
      sourceFilters.length ||
      statusFilters.length ||
      priorityFilters.length ||
      buildingFilters.length ||
      unitFilters.length ||
      labelFilters.length ||
      assigneeFilters.length ||
      supplierFilters.length ||
      (dateFilter && dateFilter !== "all") ||
      (emailStatusFilter.length && !emailStatusFilter.includes("all"))
    );
  }, [
    searchText,
    sourceFilters,
    statusFilters,
    priorityFilters,
    buildingFilters,
    unitFilters,
    labelFilters,
    assigneeFilters,
    supplierFilters,
    emailStatusFilter,
    dateFilter,
  ]);

  // Tickets for LIST view (paginated) or kanban fallback (all non-closed)
  const filteredTickets = useMemo(() => {
    return viewMode === "list" ? listTickets : nonClosedTickets;
  }, [viewMode, listTickets, nonClosedTickets]);

  // Tickets for KANBAN view (all statuses including closed)
  const kanbanTickets = useMemo(() => {
    return [...nonClosedTickets, ...closedTickets];
  }, [nonClosedTickets, closedTickets]);

  // Alle kolommen van het bord, in de volgorde van `helpdeskPhaseColumns`.
  const allStatusColumns = useMemo(() => helpdeskPhaseOrder, []);

  // Group tickets by status and slice by visible count
  const slicedKanbanTickets = useMemo(() => {
    const grouped: Record<string, HelpdeskTicketInterface[]> = {};

    allStatusColumns.forEach((status) => {
      grouped[status] = [];
    });

    kanbanTickets.forEach((ticket) => {
      const status = ticket.internalStatus as string;
      if (grouped[status]) {
        grouped[status].push(ticket);
      }
    });

    const sliced: HelpdeskTicketInterface[] = [];
    allStatusColumns.forEach((status) => {
      const ticketsInColumn = grouped[status];
      const visibleCount = visibleCountPerColumn[status] || 30;
      sliced.push(...ticketsInColumn.slice(0, visibleCount));
    });

    return sliced;
  }, [kanbanTickets, visibleCountPerColumn, allStatusColumns]);

  // Pre-calculate column counts
  const columnCountsMap = useMemo(() => {
    const counts: Record<string, number> = {};
    kanbanTickets.forEach((ticket) => {
      const status = ticket.internalStatus as string;
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [kanbanTickets]);

  // Get column counts
  const getColumnCounts = useCallback(
    (status: TicketInternalStatus) => {
      const totalInColumn = columnCountsMap[status] || 0;
      const visibleInColumn = visibleCountPerColumn[status] || 30;
      return {
        total: totalInColumn,
        visible: Math.min(visibleInColumn, totalInColumn),
        hasMore: totalInColumn > visibleInColumn,
      };
    },
    [columnCountsMap, visibleCountPerColumn],
  );

  // Display count for badges
  // In kanban mode, use kanbanTickets (includes closed) so the total matches slicedKanbanTickets
  const displayedNonClosedTickets = useMemo(() => {
    return viewMode === "kanban" ? kanbanTickets.length : filteredTickets.length;
  }, [viewMode, kanbanTickets.length, filteredTickets.length]);

  // ============ FILTER BAR CONFIGURATION ============
  const filterBarSections: FilterSection[] = useMemo(
    () => [
      {
        key: "source",
        label: "Bron",
        options: [
          { key: "Huurder", label: "Huurder" },
          { key: "Intern", label: "Intern" },
          { key: "Buur", label: "Buur" },
          { key: "Onbekend", label: "Onbekend" },
        ],
        selectedValues: sourceFilters,
        onUpdate: (values) => updateFilters("source", values),
        color: "moss" as const,
      },
      {
        key: "status",
        label: "Fase",
        // Uit `helpdeskPhaseColumns` in plaats van een handmatige kopie: die
        // liep achter zodra er een fase bijkwam, en dan kon je er niet op
        // filteren terwijl er wél tickets in stonden.
        options: [
          ...helpdeskPhaseOrder.map((fase) => ({ key: fase, label: fase })),
          { key: "Unknown", label: "Unknown" },
        ],
        selectedValues: statusFilters,
        onUpdate: (values) => updateFilters("status", values),
        color: "brick" as const,
      },
      {
        key: "priority",
        label: "Prioriteit",
        options: [
          { key: "Laag", label: "Laag" },
          { key: "Gemiddeld", label: "Gemiddeld" },
          { key: "Hoog", label: "Hoog" },
          { key: "Unknown", label: "Unknown" },
        ],
        selectedValues: priorityFilters,
        onUpdate: (values) => updateFilters("priority", values),
        color: "yellow" as const,
      },
      {
        key: "assignee",
        label: "Toegewezen aan",
        options: allAssignees.map((userId) => {
          const userName = getUserName(userId, users).name;
          return {
            key: userId,
            label: userName || userId,
            avatar: getUserImageUrlById(userId, users),
          };
        }),
        selectedValues: assigneeFilters,
        onUpdate: (values) => updateFilters("assignee", values),
        color: "neutral" as const,
        renderOption: (option) => (
          <>
            <Avatar
              className="h-4 w-4 mr-1.5"
              src={option.avatar || undefined}
            />
            <span>{option.label}</span>
          </>
        ),
      },
      {
        key: "building",
        label: "Gebouw",
        options: allBuildings.map((id) => ({ key: id, label: id })),
        selectedValues: buildingFilters,
        onUpdate: (values) => updateFilters("building", values),
        color: "neutral" as const,
      },
      {
        key: "unit",
        label: "Unit",
        options: allUnits.map((id) => ({ key: id, label: id })),
        selectedValues: unitFilters,
        onUpdate: (values) => updateFilters("unit", values),
        color: "neutral" as const,
      },
      {
        key: "label",
        label: "Label",
        options: allLabels.map((label) => ({ key: label, label: label })),
        selectedValues: labelFilters,
        onUpdate: (values) => updateFilters("label", values),
        color: "neutral" as const,
        footer: (
          <Button
            variant={labelFilters.includes("Geen label") ? "default" : "outline"}
            size="sm"
            className="h-7 text-xs"
            onClick={() =>
              setLabelFilters(
                labelFilters.includes("Geen label")
                  ? labelFilters.filter((l) => l !== "Geen label")
                  : [...labelFilters, "Geen label"]
              )
            }
          >
            Toon tickets zonder label
          </Button>
        ),
      },
      {
        key: "supplier",
        label: "Leverancier",
        options: allSuppliers
          .sort((a, b) => {
            if (a === "Geen leverancier") return -1;
            if (b === "Geen leverancier") return 1;
            return a.localeCompare(b);
          })
          .map((supplier) => ({ key: supplier, label: supplier })),
        selectedValues: supplierFilters,
        onUpdate: (values) => updateFilters("supplier", values),
        color: "neutral" as const,
      },
      {
        key: "date",
        label: "Datum",
        options: [
          { key: "all", label: "Alle datums" },
          { key: "today", label: "Vandaag" },
          { key: "this-week", label: "Deze week" },
          { key: "this-month", label: "Deze maand" },
          { key: "older-than-week", label: "Ouder dan 1 week" },
        ],
        selectedValues: dateFilter !== "all" ? [dateFilter] : [],
        onUpdate: (values) => updateFilters("date", values),
        color: "neutral" as const,
      },
      {
        key: "emailStatus",
        label: "Email Status",
        options: [
          { key: "all", label: "Alle tickets" },
          { key: "unanswered", label: "Onbeantwoorde inkomende mail" },
          { key: "unread", label: "Ongelezen berichten" },
        ],
        selectedValues: emailStatusFilter,
        onUpdate: (values) => updateFilters("emailStatus", values),
        color: "moss" as const,
      },
    ],
    [
      sourceFilters,
      statusFilters,
      priorityFilters,
      assigneeFilters,
      buildingFilters,
      unitFilters,
      labelFilters,
      supplierFilters,
      emailStatusFilter,
      dateFilter,
      allBuildings,
      allUnits,
      allLabels,
      allAssignees,
      allSuppliers,
      updateFilters,
      users, // Add users dependency for avatar rendering
    ],
  );

  // Track if filters are being restored from URL to prevent premature API calls
  const [isRestoringFromURL, setIsRestoringFromURL] = useState(false);

  // Filter chips for display
  const filterBarChips = useMemo(() => {
    const chips: any[] = [];

    sourceFilters.forEach((source) => {
      chips.push({
        key: `source-${source}`,
        label: source,
        category: "Bron",
        color: "moss" as const,
        onRemove: () =>
          setSourceFilters((prev) => prev.filter((s) => s !== source)),
      });
    });

    statusFilters.forEach((status) => {
      chips.push({
        key: `status-${status}`,
        label: status,
        category: "Fase",
        color: "brick" as const,
        onRemove: () =>
          setStatusFilters((prev) => prev.filter((s) => s !== status)),
      });
    });

    priorityFilters.forEach((priority) => {
      chips.push({
        key: `priority-${priority}`,
        label: priority,
        category: "Prioriteit",
        color: "yellow" as const,
        onRemove: () =>
          setPriorityFilters((prev) => prev.filter((p) => p !== priority)),
      });
    });

    assigneeFilters.forEach((assignee) => {
      const user = users.find((u: any) => u.fullName === assignee);
      const displayName = user?.fullName || assignee;
      chips.push({
        key: `assignee-${assignee}`,
        label: displayName,
        category: "Toegewezen aan",
        color: "neutral" as const,
        onRemove: () =>
          setAssigneeFilters((prev) => prev.filter((a) => a !== assignee)),
      });
    });

    buildingFilters.forEach((building) => {
      chips.push({
        key: `building-${building}`,
        label: building,
        category: "Gebouw",
        color: "neutral" as const,
        onRemove: () =>
          setBuildingFilters((prev) => prev.filter((b) => b !== building)),
      });
    });

    unitFilters.forEach((unit) => {
      chips.push({
        key: `unit-${unit}`,
        label: unit,
        category: "Unit",
        color: "neutral" as const,
        onRemove: () =>
          setUnitFilters((prev) => prev.filter((u) => u !== unit)),
      });
    });

    labelFilters.forEach((label) => {
      chips.push({
        key: `label-${label}`,
        label: label,
        category: "Label",
        color: "neutral" as const,
        onRemove: () =>
          setLabelFilters((prev) => prev.filter((l) => l !== label)),
      });
    });

    supplierFilters.forEach((supplier) => {
      chips.push({
        key: `supplier-${supplier}`,
        label: supplier,
        category: "Leverancier",
        color: "neutral" as const,
        onRemove: () =>
          setSupplierFilters((prev) => prev.filter((s) => s !== supplier)),
      });
    });

    emailStatusFilter.forEach((status) => {
      if (status !== "all") {
        const label =
          status === "unanswered"
            ? "Onbeantwoorde inkomende mail"
            : status === "unread"
              ? "Ongelezen berichten"
              : status;
        chips.push({
          key: `emailStatus-${status}`,
          label: label,
          category: "Email Status",
          color: "moss" as const,
          onRemove: () =>
            setEmailStatusFilter((prev) => prev.filter((s) => s !== status)),
        });
      }
    });

    if (chips.length > 0) {
      chips.unshift({
        key: "clear-all-filters",
        label: "✕ Wis alle filters",
        category: undefined,
        color: "brick" as const,
        onRemove: () => {
          setSourceFilters([]);
          setStatusFilters([]);
          setPriorityFilters([]);
          setAssigneeFilters([]);
          setBuildingFilters([]);
          setUnitFilters([]);
          setLabelFilters([]);
          setSupplierFilters([]);
          setEmailStatusFilter([]);
          setSearchText("");
        },
      });
    }

    return chips;
  }, [
    sourceFilters,
    statusFilters,
    priorityFilters,
    assigneeFilters,
    buildingFilters,
    unitFilters,
    labelFilters,
    supplierFilters,
    emailStatusFilter,
    users,
  ]);

  // URL restoration handler
  const handleFiltersFromURL = useCallback((urlFilters: any) => {
    console.log(
      "🔄 [useHelpdeskShared] URL restoration triggered with:",
      urlFilters,
    );

    setIsRestoringFromURL(true);

    if (urlFilters.search !== undefined) {
      setSearchText(urlFilters.search);
    }

    const sections = urlFilters.filterSections || {};
    setSourceFilters(sections.source || []);
    setStatusFilters(sections.status || []);
    setPriorityFilters(sections.priority || []);
    setAssigneeFilters(sections.assignee || []);
    setBuildingFilters(sections.building || []);
    setUnitFilters(sections.unit || []);
    setLabelFilters(sections.label || []);
    setDateFilter(sections.date?.[0] || "all");

    // Reset flag after filters are restored
    setTimeout(() => setIsRestoringFromURL(false), 100);
  }, []);

  // ============ SMART INSIGHTS ============
  const {
    smartInsightsFilter,
    setSmartInsightsFilter,
    clearSmartInsightsFilter,
    savedSearches,
    saveCurrentSearch,
    applySavedSearch,
  } = useHelpdeskFilters(nonClosedTickets);

  // ============ BULK ACTIONS ============
  const {
    selectedOpenTickets,
    selectedCompletionTickets,
    setSelectedOpenTickets,
    setSelectedCompletionTickets,
    getBulkActions,
  } = useHelpdeskBulkActions(
    handleMoveToCompletion,
    handleRevertToOpen,
    refreshTickets,
    () => {
      setExcelExportModalOpen(true);
    },
  );

  const handleExcelExportWithColumns = useCallback(
    async (selectedColumnIds: string[]) => {
      try {
        // Safety check: Ensure selectedColumnIds is an array
        if (!Array.isArray(selectedColumnIds)) {
          console.error(
            "handleExcelExportWithColumns: selectedColumnIds is not an array",
            selectedColumnIds,
          );
          toast.error("Fout bij exporteren: ongeldige kolomselectie");
          return;
        }

        // Get all selected ticket IDs with null safety
        const allSelectedTicketIds = [
          ...Array.from(selectedOpenTickets || []),
          ...Array.from(selectedCompletionTickets || []),
        ].filter((id) => id != null && typeof id === "number"); // Remove null/undefined IDs

        if (allSelectedTicketIds.length === 0) {
          toast.warning("Geen tickets geselecteerd om te exporteren");
          return;
        }

        if (selectedColumnIds.length === 0) {
          toast.warning("Geen kolommen geselecteerd om te exporteren");
          return;
        }

        console.log(
          `📤 Exporting ${allSelectedTicketIds.length} tickets with ${selectedColumnIds.length} columns`,
        );

        // Call new backend endpoint that fetches data by IDs
        // This significantly reduces payload size (only IDs sent instead of full ticket data)
        const response = await fetchWithAuth(
          `${API_URL}/excel/export-helpdesk-tickets`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ticketIds: allSelectedTicketIds,
              selectedColumnIds: selectedColumnIds,
            }),
          },
          session,
        );

        if (response.ok) {
          // Handle file download
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);

          // Create temporary download link
          const link = document.createElement("a");
          link.href = url;
          link.download = `helpdesk-tickets-${new Date().toISOString().split("T")[0]}.xlsx`;

          // Trigger download
          document.body.appendChild(link);
          link.click();

          // Cleanup
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          toast.success(
            `${allSelectedTicketIds.length} tickets geëxporteerd naar Excel`,
          );
        } else {
          const errorData = await response
            .json()
            .catch(() => ({ message: "Unknown error" }));
          throw new Error(errorData.message || "Export failed");
        }
      } catch (error) {
        console.error("handleExcelExportWithColumns: Unexpected error:", error);
        toast.error("Fout bij exporteren naar Excel. Probeer het opnieuw.");
      }
    },
    [selectedOpenTickets, selectedCompletionTickets, session],
  );

  // Render bulk action icons
  const renderBulkActionIcon = useCallback((iconType: string) => {
    switch (iconType) {
      case "changePhase":
        return (
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
            />
          </svg>
        );
      case "moveToCompletion":
        return (
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        );
      case "revertToOpen":
        return (
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        );
      case "delete":
        return (
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        );
      case "export":
        return (
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        );
      default:
        return null;
    }
  }, []);

  const handleBulkPhaseChange = useCallback(() => {
    setBulkPhasePickerOpen(true);
  }, []);

  const handleBulkPhaseConfirm = useCallback(
    async (newPhase: TicketInternalStatus) => {
      const openTicketIds = Array.from(selectedOpenTickets);
      const completionTicketIds = Array.from(selectedCompletionTickets);
      const allTicketIds = [...openTicketIds, ...completionTicketIds];

      if (allTicketIds.length === 0) {
        toast.error("Geen tickets geselecteerd");
        return;
      }

      try {
        const updatePromises = allTicketIds.map((id) =>
          fetchWithAuth(
            `${API_URL}/helpdesk/ticket/${id}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ internalStatus: newPhase }),
            },
            session,
          ),
        );

        await Promise.all(updatePromises);

        setSelectedOpenTickets(new Set());
        setSelectedCompletionTickets(new Set());
        setBulkPhasePickerOpen(false);

        await refreshTickets();

        toast.success(
          `${allTicketIds.length} ticket${allTicketIds.length > 1 ? "s" : ""} bijgewerkt naar "${getStatusLabel(newPhase)}"`,
        );
      } catch (error) {
        console.error("Error updating ticket phases:", error);
        toast.error("Fout bij bijwerken van ticket fases");
      }
    },
    [
      selectedOpenTickets,
      selectedCompletionTickets,
      setSelectedOpenTickets,
      setSelectedCompletionTickets,
      session,
      refreshTickets,
    ],
  );

  const bulkActions = useMemo(() => {
    return getBulkActions(handleBulkPhaseChange).map((action) => ({
      ...action,
      icon: renderBulkActionIcon(action.iconType),
      variant: action.variant as
        | "yellow"
        | "blue"
        | "red"
        | "primaryMoss"
        | "primaryBrick"
        | "secondaryNeutral"
        | undefined,
    }));
  }, [getBulkActions, renderBulkActionIcon, handleBulkPhaseChange]);

  // ============ EVENT HANDLERS ============
  const handleSmartInsights = useCallback(
    async (ticket: HelpdeskTicketInterface) => {
      console.log("🔍 Smart insights requested for ticket:", ticket.id);

      try {
        const response = await fetchWithAuth(
          `${API_URL}/helpdesk/ticket/${ticket.id}/smart-insights`,
          { method: "GET" },
          session,
        );

        if (response.ok) {
          const insights = await response.json();
          const similarTicketIds =
            insights.similarTickets?.map((t: any) => t.id) || [];
          const batchCandidateIds =
            insights.batchCandidates?.map((t: any) => t.id) || [];
          const allRelatedTicketIds = [
            ticket.id,
            ...similarTicketIds,
            ...batchCandidateIds,
          ];
          const uniqueTicketIds = [...new Set(allRelatedTicketIds)];

          setSmartInsightsFilter({
            isActive: true,
            ticketIds: uniqueTicketIds,
            sourceTicketId: ticket.id || null,
            sourceTicketTitle: ticket.title,
          });

          toast.success(
            `Weergave gefilterd: ${similarTicketIds.length} vergelijkbare tickets en ${batchCandidateIds.length} batch kandidaten voor H${ticket.id}`,
          );
        } else {
          throw new Error("Failed to fetch insights");
        }
      } catch (error) {
        console.error("Error fetching smart insights:", error);
        toast.error("Fout bij ophalen van slimme inzichten");
      }
    },
    [session, setSmartInsightsFilter],
  );

  const handleMerge = useCallback((ticket: HelpdeskTicketInterface) => {
    setMergeSource(ticket);
    setMergeTarget(null);
    setMergeSearchSource("");
    setMergeSearchTarget("");
    setMergeModalOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (ticket: HelpdeskTicketInterface) => {
      try {
        const emailsCount = (ticket as any).emailCount || 0;
        const notesResp = await fetchWithAuth(
          `${API_URL}/helpdesk/ticket/${ticket.id}/action-notes`,
          { method: "GET" },
          session,
        );
        const notes = notesResp.ok ? await notesResp.json() : [];
        setDeleteModal({
          open: true,
          ticket,
          emails: emailsCount,
          notes: notes.length,
        });
      } catch (e) {
        setDeleteModal({
          open: true,
          ticket,
          emails: (ticket as any).emailCount || 0,
          notes: 0,
        });
      }
    },
    [session],
  );

  const handleCreateInsurance = useCallback(
    (ticket: HelpdeskTicketInterface) => {
      setSelectedTicketForInsurance(ticket);
      setInsuranceModalOpen(true);
    },
    [],
  );

  const handleQuickAdd = useCallback(() => {
    setQuickAddModalOpen(true);
  }, []);

  const handleTicketClick = (ticket: HelpdeskTicketInterface) => {
    const currentParams = window.location.search;
    window.location.href = `/helpdesk/${ticket.id}${currentParams}`;
  };

  // ============ KANBAN HANDLERS ============
  const handleIncreaseFontSize = useCallback(() => {
    setKanbanFontSize((prev) => Math.min(prev + 1, 16));
  }, []);

  const handleDecreaseFontSize = useCallback(() => {
    setKanbanFontSize((prev) => Math.max(prev - 1, 10));
  }, []);

  const handleToggleColumnVisibility = useCallback((columnId: string) => {
    setHiddenKanbanColumns((prev) => {
      if (prev.includes(columnId)) {
        return prev.filter((id) => id !== columnId);
      } else {
        return [...prev, columnId];
      }
    });
  }, []);

  const handleLoadMoreForColumn = useCallback(
    (status: TicketInternalStatus) => {
      setVisibleCountPerColumn((prev) => ({
        ...prev,
        [status]: (prev[status] || 30) + 30,
      }));
    },
    [],
  );

  const columnPagination = useMemo(() => {
    return allStatusColumns.map((status) => {
      const counts = getColumnCounts(status);
      return {
        columnId: status,
        hasMore: counts.hasMore,
        isLoadingMore: false,
        onLoadMore: () => handleLoadMoreForColumn(status),
        loadMoreText: "30 meer laden...",
        loadingText: "Laden...",
      };
    });
  }, [allStatusColumns, getColumnCounts, handleLoadMoreForColumn]);

  const handleColumnHeaderClick = useCallback(
    (status: TicketInternalStatus) => {
      setSelectedColumnPhase(status);
      setColumnPhasePickerOpen(true);
    },
    [],
  );

  const handleColumnPhaseConfirm = useCallback(
    async (newPhase: TicketInternalStatus) => {
      if (!selectedColumnPhase) {
        toast.error("Geen kolom geselecteerd");
        return;
      }

      const allTickets = [
        ...nonClosedTickets,
        ...closedTickets,
      ];
      const ticketsInColumn = allTickets.filter(
        (ticket: HelpdeskTicketInterface) =>
          ticket.internalStatus === selectedColumnPhase,
      );

      if (ticketsInColumn.length === 0) {
        toast.info(
          `Geen tickets gevonden in kolom "${getStatusLabel(selectedColumnPhase)}"`,
        );
        setColumnPhasePickerOpen(false);
        return;
      }

      try {
        const updatePromises = ticketsInColumn.map(
          (ticket: HelpdeskTicketInterface) =>
            fetchWithAuth(
              `${API_URL}/helpdesk/ticket/${ticket.id}`,
              {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ internalStatus: newPhase }),
              },
              session,
            ),
        );

        await Promise.all(updatePromises);

        setColumnPhasePickerOpen(false);
        setSelectedColumnPhase(null);

        await refreshTickets();

        toast.success(
          `${ticketsInColumn.length} ticket${ticketsInColumn.length > 1 ? "s" : ""} verplaatst van "${getStatusLabel(selectedColumnPhase)}" naar "${getStatusLabel(newPhase)}"`,
        );
      } catch (error) {
        console.error("Error updating column tickets:", error);
        toast.error("Fout bij bijwerken van tickets");
      }
    },
    [
      selectedColumnPhase,
      nonClosedTickets,
      closedTickets,
      session,
      refreshTickets,
    ],
  );

  // ============ COLUMN ACTIONS ============
  const columnActions: ColumnActionConfig[] = useMemo(
    () => [
      {
        field: "label",
        onAfterUpdate: async (labelValue, ticketIds, context) => {
          if (
            !labelValue ||
            labelValue === "UNKNOWN" ||
            ticketIds.length === 0
          ) {
            return;
          }

          try {
            const response = await fetchWithAuth(
              `${API_URL}/helpdesk/suppliers/frequent-by-label/${encodeURIComponent(labelValue)}`,
              { method: "GET" },
              session,
            );

            if (response.ok) {
              const suggestions = await response.json();
              if (suggestions.length > 0) {
                context.openModal({
                  type: "supplier-suggestion",
                  data: { suggestions, label: labelValue, ticketIds },
                });
              }
            }
          } catch (error) {
            console.error("Error fetching supplier suggestions:", error);
          }
        },
      },
    ],
    [session],
  );

  const handleModalRequest = useCallback((event: { config: ModalConfig }) => {
    const { config } = event;

    switch (config.type) {
      case "supplier-suggestion":
        const { suggestions, label, ticketIds } = config.data;
        setSuggestedSuppliers(suggestions);
        setSuggestionForLabel(label);
        setSuggestionForTicketIds(ticketIds);
        setSupplierSuggestionOpen(true);
        break;

      default:
        console.warn(
          "[useHelpdeskShared] Unknown modal type requested:",
          config.type,
        );
    }
  }, []);

  const applySuggestedSupplier = useCallback(
    async (supplier: any) => {
      setIsUpdatingSupplier(true);
      try {
        const updatePromises = suggestionForTicketIds.map(async (id) => {
          try {
            const supplierId = supplier.company?.id ?? supplier.id;
            const response = await fetchWithAuth(
              `${API_URL}/helpdesk/${id}/suppliers`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ supplierId }),
              },
              session,
            );

            if (!response.ok) {
              throw new Error(`Failed to update ticket ${id}`);
            }

            return await response.json();
          } catch (error) {
            console.error(`Error updating ticket ${id}:`, error);
            throw error;
          }
        });

        await Promise.all(updatePromises);

        setSupplierSuggestionOpen(false);
        setSuggestedSuppliers([]);
        setSuggestionForLabel(null);
        setSuggestionForTicketIds([]);

        await refreshTickets();

        toast.success(
          `Leverancier "${supplier.company?.name || "Onbekend"}" toegewezen aan ${suggestionForTicketIds.length} ticket${suggestionForTicketIds.length > 1 ? "s" : ""}`,
        );
      } catch (error) {
        console.error("Error applying suggested supplier:", error);
        toast.error("Fout bij toewijzen van leverancier");
      } finally {
        setIsUpdatingSupplier(false);
      }
    },
    [suggestionForTicketIds, session, refreshTickets],
  );

  // ============ RETURN STATE ============
  return {
    // De filters zoals ze naar de API gaan. De weergave per gebouw haalt haar
    // eigen (gegroepeerde) lijst op en heeft daar exact dezelfde filters voor
    // nodig — anders toont van weergave wisselen stil een andere selectie.
    filters,

    // Data
    nonClosedTickets,
    closedTickets,
    isLoadingTickets,
    totalCount,
    openCount,
    completionCount,
    isLoadingTotalCount,

    // List pagination
    listPage,
    setListPage,
    listPageSize,
    isListLoading,
    listTotal,

    // Computed data
    filteredTickets,
    kanbanTickets,
    slicedKanbanTickets,
    displayedNonClosedTickets,
    hasFilters,

    // Filter states
    searchText,
    setSearchText,
    sourceFilters,
    setSourceFilters,
    statusFilters,
    setStatusFilters,
    priorityFilters,
    setPriorityFilters,
    buildingFilters,
    setBuildingFilters,
    unitFilters,
    setUnitFilters,
    labelFilters,
    setLabelFilters,
    assigneeFilters,
    setAssigneeFilters,
    supplierFilters,
    setSupplierFilters,
    dateFilter,
    setDateFilter,
    emailStatusFilter,
    setEmailStatusFilter,

    // Filter options
    allBuildings,
    allUnits,
    allLabels,
    allAssignees,
    allSuppliers,

    // Filter configuration
    filterBarSections,
    filterBarChips,
    updateFilters,
    handleFiltersFromURL,
    isRestoringFromURL,

    // Smart insights
    smartInsightsFilter,
    setSmartInsightsFilter,
    clearSmartInsightsFilter,
    savedSearches,
    saveCurrentSearch,
    applySavedSearch,

    // Actions
    refreshTickets,
    handleCompleteTicket,
    handleMoveToCompletion,
    handleRevertToOpen,
    handleUpdateTicket,
    handleSmartInsights,
    handleMerge,
    handleDelete,
    handleCreateInsurance,
    handleQuickAdd,
    handleTicketClick,

    // Bulk actions
    selectedOpenTickets,
    selectedCompletionTickets,
    setSelectedOpenTickets,
    setSelectedCompletionTickets,
    bulkActions,
    handleBulkPhaseChange,
    handleBulkPhaseConfirm,
    handleExcelExportWithColumns,

    // Kanban-specific
    visibleCountPerColumn,
    setVisibleCountPerColumn,
    hiddenKanbanColumns,
    setHiddenKanbanColumns,
    kanbanFontSize,
    setKanbanFontSize,
    handleIncreaseFontSize,
    handleDecreaseFontSize,
    handleToggleColumnVisibility,
    allStatusColumns,
    columnCountsMap,
    getColumnCounts,
    handleLoadMoreForColumn,
    columnPagination,
    handleColumnHeaderClick,
    handleColumnPhaseConfirm,

    // Modal states
    quickAddModalOpen,
    setQuickAddModalOpen,
    mergeModalOpen,
    setMergeModalOpen,
    mergeSource,
    setMergeSource,
    mergeTarget,
    setMergeTarget,
    mergeSearchSource,
    setMergeSearchSource,
    mergeSearchTarget,
    setMergeSearchTarget,
    deleteModal,
    setDeleteModal,
    insuranceModalOpen,
    setInsuranceModalOpen,
    selectedTicketForInsurance,
    setSelectedTicketForInsurance,
    supplierSuggestionOpen,
    setSupplierSuggestionOpen,
    suggestedSuppliers,
    setSuggestedSuppliers,
    suggestionForLabel,
    setSuggestionForLabel,
    suggestionForTicketIds,
    setSuggestionForTicketIds,
    isUpdatingSupplier,
    setIsUpdatingSupplier,
    excelExportModalOpen,
    setExcelExportModalOpen,
    bulkPhasePickerOpen,
    setBulkPhasePickerOpen,
    columnPhasePickerOpen,
    setColumnPhasePickerOpen,
    selectedColumnPhase,
    setSelectedColumnPhase,

    // Column actions
    columnActions,
    handleModalRequest,
    applySuggestedSupplier,
    renderBulkActionIcon,

    // Users
    users,
  };
};
